/**
 * KonvaCanvas — chunks 1 + 2 of the SVG → Konva migration.
 *
 * Chunk 1 (bfd459c, c9aa43a) shipped READ-ONLY parity with
 * Canvas.tsx's raster surface: figure raster + every existing
 * canvas primitive renders on a Konva.Stage; the embedded chrome
 * (Toolbar / ActionBench / StatusStrip) wires the existing store
 * actions; pan + cursor-anchored wheel zoom both work.
 *
 * Chunk 2 wires the 6 surviving tools — cursor (select + drag),
 * arrow (two-click commit), curved_arrow (two-click + draggable
 * bezier handles after select), text (HTML <input> overlay +
 * commit on Enter/blur), lasso (drag-rectangle + AABB overlap
 * select; transient, never persisted), zoom (wheel + buttons +
 * fit-to-screen) — plus undo/redo (Cmd+Z / Cmd+Shift+Z), the
 * Delete-key deletion path, the IDB undo-stack hydration, the
 * focus-driven cross-tab refetch, and the term-drag drop
 * handler.
 *
 * Chunk 3 absorbed vector rendering: the VectorEditSurface
 * delegation is gone; KonvaCanvas now renders BOTH raster and
 * vector modes inline on one Stage. Vector mode is an HTML SVG
 * layer rendered BEHIND the transparent Konva Stage; both share
 * the same {scale, pan} transform so chunk-2 primitives layer
 * uniformly on top of either mode. Pinch zoom is wired explicitly
 * (Konva doesn't auto-handle 2-finger touch) and works in both
 * modes. Display normalisation: small figures (< 1200×900) scale
 * UP at fit-to-screen so numerals + arrows + text feel
 * proportionate; large figures keep their natural fit scale.
 *
 * Default canvas as of the chunk-3-followup soak. The legacy
 * Canvas.tsx is opt-in via ``?canvas=svg`` for emergency rollback
 * during the soak window — see frontend/src/config/features.ts.
 *
 * Same prop interface as Canvas.tsx: ``{matterId, figure}`` only.
 * Every other piece of state is pulled from ``useDrawingStore`` —
 * matches the existing convention so the consumer-side switch
 * stays a one-line change in DrawingPage.
 *
 * Read-only contract for chunk 1:
 *   - Konva nodes are constructed with ``listening={false}`` so
 *     Stage.getIntersection returns null and React-Konva doesn't
 *     route pointer events to the chip / arrow / text shapes.
 *   - Selection ring / stale hatch / focus pulse / orphan tooltip
 *     and other interaction affordances from Canvas.tsx's
 *     NumeralChip are deliberately omitted; they belong with the
 *     interactive port in chunk 2.
 *   - Vector view (figure.is_vectorized && vectorView) keeps
 *     delegating to VectorEditSurface — same gate Canvas.tsx
 *     uses. KonvaCanvas only owns the raster path.
 *
 * Layer ordering (bottom → top) — matches Konva's parent-first
 * draw order and the SVG z-index expectation:
 *   1. Background image — figure raster as Konva.Image
 *   2. Primitives — sorted by z_index ascending; chips, arrows,
 *      etc. layered in the order ``drawing_canvas_state.z_index``
 *      stores
 *
 * Zoom + pan:
 *   - Wheel zooms toward the cursor (preserves the canvas-space
 *     point under the pointer); clamped to [0.25, 4] matching
 *     drawingStore.setZoom's existing range.
 *   - Drag on empty stage pans. Konva's Stage.draggable handles
 *     this natively — we leave it on by default.
 *   - zoomLevel mirrors useDrawingStore so a tool-bar zoom button
 *     in Canvas.tsx and the wheel zoom on KonvaCanvas stay in
 *     sync if the drafter switches surfaces. Pan is local — the
 *     existing Canvas.tsx also keeps pan local (component state),
 *     not store-backed.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Arrow as _Arrow,
  Circle as _Circle,
  Group as _Group,
  Image as _KImage,
  Layer as _Layer,
  Line as _Line,
  Rect as _Rect,
  Stage as _Stage,
  Text as _Text,
} from 'react-konva';
import type Konva from 'konva';

// React-konva's TypeScript surface (v19.2 against React 19 +
// @types/react 19) declares each component as ``React.FC<P>``.
// React 19 dropped the implicit ``children`` from React.FC, so the
// strict TS-build at ``craco build`` rejects every nested
// ``<Stage>…<Layer>…</Layer></Stage>`` tree with TS2322 ("Property
// 'children' does not exist on type StageProps & …"). The runtime
// is fine — Konva renders children correctly via its own
// reconciler. Until react-konva's types ship a children-aware
// definition, re-cast every component once at import to a generic
// FC that accepts children. Keeps the call sites idiomatic without
// scattering ``as any`` or ``// @ts-expect-error`` across the
// JSX. Identical pattern to the wrapper trick used elsewhere in
// the codebase for Radix forwardRef bindings under React 19
// (frontend/src/components/drawings/DrawingPage.tsx via
// ``DropdownMenu`` casts in commit 758f89c).
type _AnyFC = React.FC<{ children?: React.ReactNode } & Record<string, unknown>>;
const Stage = _Stage as unknown as _AnyFC;
const Layer = _Layer as unknown as _AnyFC;
const Group = _Group as unknown as _AnyFC;
const Image = _KImage as unknown as _AnyFC;
const Circle = _Circle as unknown as _AnyFC;
const Text = _Text as unknown as _AnyFC;
const Arrow = _Arrow as unknown as _AnyFC;
const Line = _Line as unknown as _AnyFC;
const Rect = _Rect as unknown as _AnyFC;
import type {
  CanvasPrimitive,
  DrawingFigureSummary,
} from '@/api/client';
import { getDrawingFigureSvg } from '@/api/client';
// SEC-F01 (Phase 25 3-C) — DOMPurify wraps SVG bytes before
// dangerouslySetInnerHTML. If any upstream step (figure upload,
// IDF extraction, AI generation) passed through an SVG with
// embedded <script> tags or onload/onerror event attributes,
// this is a stored XSS vector — sanitisation closes it.
import DOMPurify from 'dompurify';
import {
  checkForCrossTabWrite,
  hydrateUndoStacksFromStorage,
  useDrawingStore,
  type DrawingTool,
} from '@/store/drawingStore';
import {
  DRAWING_TERM_DRAG_TYPE,
  type DraggedTermPayload,
} from './ReferencePanel';
// Chunk 3 — VectorEditSurface delegation removed. KonvaCanvas
// now renders BOTH raster + vector modes inline on one Stage:
// the vector path is an HTML SVG layer behind a transparent
// Konva Stage, both driven by the same {scale, pan} transform.
// VectorEditSurface.tsx stays on disk (still imported by
// Canvas.tsx) and gets deleted in chunk 6 alongside Canvas.tsx.
import {
  defaultBezierControl,
  isRasterAnchor,
  primitiveBoundingBox,
  quadBezierPoints,
} from './_canvas_geom';
// Chrome reused from Canvas.tsx so KonvaCanvas renders identical
// toolbar / action bench / status strip + figure pager. Chunk 1
// landed read-only and pinned cursor tool; chunk 2 routes the
// active tool from the shared store and wires all 6 tool flows.
import { Toolbar, ActionBench, StatusStrip } from './Canvas';

interface Props {
  matterId: string;
  figure: DrawingFigureSummary | null;
}

type Pt = { x: number; y: number };


/** Default fallback dimensions for figures whose ``width`` /
 *  ``height`` columns aren't yet populated AND whose raster image
 *  hasn't loaded — kept small so the empty-state placeholder
 *  doesn't claim half the viewport. Replaced with the figure row's
 *  stored dimensions or the natural image size as soon as either
 *  is known. */
const FALLBACK_W = 800;
const FALLBACK_H = 600;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 1.1;

// Chunk 3 — display normalisation. Small figures (e.g. 800×600
// IDF imports) used to render too small at fit-zoom; numeral
// chips and arrows then rendered tiny relative to the available
// viewport area. The fit-to-screen helper now treats every
// figure as if it occupies AT LEAST a 1200×900 canvas so small
// figures scale UP to roughly half a 1440p display's drawable
// area. Large figures (≥ 1200 wide AND ≥ 900 tall) keep their
// natural fit-to-viewport scale — no regression.
//
// Underlying canvas-pixel coords for primitives are unchanged;
// this is purely a fit-zoom calculation. See
// docs/WORKBENCH_SIMPLIFICATION_NOTES.md "Display normalisation"
// for the rationale + numbers.
const NORMALIZED_FIT_W = 1200;
const NORMALIZED_FIT_H = 900;


function _computeFitScale(
  viewportW: number, viewportH: number,
  figureW: number, figureH: number,
): number {
  if (viewportW <= 0 || viewportH <= 0) return 1;
  if (figureW <= 0 || figureH <= 0) return 1;
  const targetW = Math.max(figureW, NORMALIZED_FIT_W);
  const targetH = Math.max(figureH, NORMALIZED_FIT_H);
  const scale = Math.min(viewportW / targetW, viewportH / targetH);
  // Clamp into the same [ZOOM_MIN, ZOOM_MAX] range setZoom uses
  // so a fit on a tiny viewport doesn't push the zoom below the
  // floor.
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale));
}


/** Fetch the figure's SVG bytes when vector mode is active.
 *  Empty string while loading or on error so the caller can
 *  render the SVG layer as a no-op until the bytes land.
 *
 *  The re-fetch signal comes from drawingStore.svgRefreshByFigure
 *  (a per-figure counter bumped by the vectorize success path
 *  and the SVG-PATCH path), NOT from figure.updated_at. Canvas
 *  writes also bump figure.updated_at — using that as the dep
 *  caused the chunk-3 "vector flash" where every annotation
 *  re-fetched the SVG. The store signal only bumps when the
 *  SVG bytes themselves change. */
function useFigureSvg(
  matterId: string,
  figureId: string | null,
  refreshKey: number,
  enabled: boolean,
): string {
  const [svg, setSvg] = useState<string>('');
  useEffect(() => {
    if (!enabled || !figureId) { setSvg(''); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        // Pass refreshKey as the cache-bust param so the
        // browser's HTTP cache doesn't serve a stale SVG after
        // a PATCH (the GET URL is otherwise identical across
        // calls — FastAPI's FileResponse only sends ETag /
        // Last-Modified, which the browser may not revalidate).
        const text = await getDrawingFigureSvg(
          matterId, figureId, refreshKey,
        );
        if (!cancelled) setSvg(_annotateSvg(text));
      } catch {
        if (!cancelled) setSvg('');
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, matterId, figureId, refreshKey]);
  return svg;
}


// ─── SVG annotation + hit-map (chunk-3 followup vector edits) ─
// Ported from VectorEditSurface (deleted in chunk 6). Stamps
// ``data-svg-edit-id`` on every visible leaf element so the
// Konva-side hit layer can address each one. The SVG layer
// itself stays pointer-events:none — Konva owns interaction.

const _SELECTABLE_TAGS = new Set([
  'path', 'rect', 'circle', 'ellipse',
  'line', 'polyline', 'polygon', 'text',
]);
const _SKIP_DESCEND_TAGS = new Set([
  'defs', 'style', 'title', 'desc', 'metadata',
  'clippath', 'mask', 'filter', 'lineargradient', 'radialgradient',
]);


function _annotateSvg(svgText: string): string {
  if (!svgText || !svgText.trim()) return svgText;
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const root = doc.documentElement;
    if (!root || root.nodeName.toLowerCase() !== 'svg') return svgText;
    let i = 0;
    const walk = (el: Element): void => {
      const tag = el.tagName.toLowerCase();
      if (_SKIP_DESCEND_TAGS.has(tag)) return;
      if (_SELECTABLE_TAGS.has(tag)) {
        el.setAttribute('data-svg-edit-id', `el-${i++}`);
        return;
      }
      Array.from(el.children).forEach(walk);
    };
    Array.from(root.children).forEach(walk);
    return new XMLSerializer().serializeToString(doc);
  } catch {
    return svgText;
  }
}


function _removeElementsFromSvg(
  svgText: string, editIds: Set<string>,
): string {
  if (!svgText || editIds.size === 0) return svgText;
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    let removed = 0;
    editIds.forEach((id) => {
      const target = doc.querySelector(`[data-svg-edit-id="${id}"]`);
      if (target) {
        target.remove();
        removed += 1;
      }
    });
    if (removed === 0) return svgText;
    // Strip ALL data-svg-edit-id stamps before persisting; the
    // next fetch + _annotateSvg will re-apply fresh ids.
    doc.querySelectorAll('[data-svg-edit-id]').forEach((el) => {
      el.removeAttribute('data-svg-edit-id');
    });
    return new XMLSerializer().serializeToString(doc);
  } catch {
    return svgText;
  }
}


interface SvgHitEntry {
  editId: string;
  bbox: { x: number; y: number; width: number; height: number };
}

interface SvgHitMap {
  entries: SvgHitEntry[];
  /** Internal SVG coord-space size (from the root viewBox or
   *  width/height attrs). Drives the Konva.Group scale that
   *  maps internal coords → canvas-pixel coords. */
  internalW: number;
  internalH: number;
}


/** Walk the rendered SVG (post-mount) and build a hit map: one
 *  entry per ``data-svg-edit-id`` leaf with its bounding box in
 *  SVG-internal coordinates. Re-runs whenever svgContent
 *  changes — the rendered DOM is the source of truth for
 *  ``getBBox``. */
function useSvgHitMap(
  svgContent: string,
  containerRef: React.MutableRefObject<HTMLDivElement | null>,
): SvgHitMap {
  const [map, setMap] = useState<SvgHitMap>({
    entries: [], internalW: 0, internalH: 0,
  });
  useEffect(() => {
    if (!svgContent) {
      setMap({ entries: [], internalW: 0, internalH: 0 });
      return;
    }
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    // Determine the SVG's internal coord space. viewBox wins;
    // fall back to width/height attrs as numbers; finally to
    // the bbox of the root.
    let internalW = 0;
    let internalH = 0;
    const vb = svgEl.getAttribute('viewBox');
    if (vb) {
      const parts = vb.trim().split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
        internalW = parts[2];
        internalH = parts[3];
      }
    }
    if (internalW <= 0 || internalH <= 0) {
      const w = parseFloat(svgEl.getAttribute('width') || '0');
      const h = parseFloat(svgEl.getAttribute('height') || '0');
      if (w > 0 && h > 0) { internalW = w; internalH = h; }
    }
    if (internalW <= 0 || internalH <= 0) {
      try {
        const rb = (svgEl as unknown as SVGGraphicsElement).getBBox();
        internalW = rb.width;
        internalH = rb.height;
      } catch { /* defensive */ }
    }
    const entries: SvgHitEntry[] = [];
    // CTM-aware bbox: getBBox returns the element's LOCAL
    // coords. Vectorize traces wrap output in nested <g>
    // containers (often with transforms) — using local coords
    // here positions hit Rects somewhere other than where the
    // path actually paints, and the user's click misses the
    // hit Rect entirely. Project the four bbox corners
    // through the element's CTM to get its bbox in SVG-root
    // coords, then take the AABB.
    const svgRoot = svgEl as unknown as SVGSVGElement;
    const pt = svgRoot.createSVGPoint();
    svgEl.querySelectorAll('[data-svg-edit-id]').forEach((el) => {
      try {
        const graphic = el as SVGGraphicsElement;
        const local = graphic.getBBox();
        if (local.width <= 0 || local.height <= 0) return;
        const ctm = graphic.getCTM();
        if (!ctm) return;
        const corners: Array<[number, number]> = [
          [local.x, local.y],
          [local.x + local.width, local.y],
          [local.x, local.y + local.height],
          [local.x + local.width, local.y + local.height],
        ];
        const xs: number[] = [];
        const ys: number[] = [];
        for (const [cx, cy] of corners) {
          pt.x = cx; pt.y = cy;
          const projected = pt.matrixTransform(ctm);
          xs.push(projected.x);
          ys.push(projected.y);
        }
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        const width = maxX - minX;
        const height = maxY - minY;
        if (width <= 0 || height <= 0) return;
        entries.push({
          editId: el.getAttribute('data-svg-edit-id') || '',
          bbox:   { x: minX, y: minY, width, height },
        });
      } catch { /* element with no geometry; skip */ }
    });
    setMap({ entries, internalW, internalH });
  }, [svgContent, containerRef]);
  return map;
}


/** Lightweight hook that loads the raster URL and yields the
 *  ``HTMLImageElement`` once decoding completes. Returns ``null``
 *  while loading or on error so the caller renders a placeholder
 *  (or simply omits the Konva.Image). Re-fetches on URL change.
 *  Avoids the ``use-image`` extra dependency — straight ``Image()``
 *  + state. */
function useRasterImage(url: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) { setImg(null); return; }
    let cancelled = false;
    const el = new window.Image();
    // ``crossOrigin`` left at the browser default — the raster
    // endpoint is same-origin in production (proxied by Nginx) so
    // CORS isn't a concern. If a future deploy serves drawings
    // from a CDN, we'll need ``el.crossOrigin = 'anonymous'`` so
    // Konva's getImageData / toDataURL operations don't taint
    // the canvas. Out of scope for chunk 1 (read-only).
    el.onload = () => { if (!cancelled) setImg(el); };
    el.onerror = () => { if (!cancelled) setImg(null); };
    el.src = url;
    return () => { cancelled = true; };
  }, [url]);
  return img;
}


// Geometry helpers (defaultBezierControl, quadBezierPoints,
// primitiveBoundingBox) live in ./_canvas_geom and are shared
// with Canvas.tsx so both surfaces keep byte-identical math.


export default function KonvaCanvas({ matterId, figure }: Props) {
  const canvasPrimitives = useDrawingStore((s) => s.canvasPrimitives);
  const numerals = useDrawingStore((s) => s.numerals);
  const zoomLevel = useDrawingStore((s) => s.zoomLevel);
  const setZoom = useDrawingStore((s) => s.setZoom);
  const figures = useDrawingStore((s) => s.figures);
  const undoCanvas = useDrawingStore((s) => s.undoCanvas);
  const redoCanvas = useDrawingStore((s) => s.redoCanvas);
  const canUndoFn = useDrawingStore((s) => s.canUndo);
  const canRedoFn = useDrawingStore((s) => s.canRedo);
  const activeTool = useDrawingStore((s) => s.activeTool);
  const setActiveTool = useDrawingStore((s) => s.setActiveTool);
  const upsertCanvasPrimitive = useDrawingStore(
    (s) => s.upsertCanvasPrimitive,
  );
  const deletePrimitives = useDrawingStore((s) => s.deletePrimitives);
  const beginCanvasMutationGroup = useDrawingStore(
    (s) => s.beginCanvasMutationGroup,
  );
  const endCanvasMutationGroup = useDrawingStore(
    (s) => s.endCanvasMutationGroup,
  );
  const selectedPrimitiveIds = useDrawingStore(
    (s) => s.selectedPrimitiveIds,
  );
  const setSelectedIds = useDrawingStore((s) => s.setSelectedIds);
  const clearSelection = useDrawingStore((s) => s.clearSelection);
  const showToast = useDrawingStore((s) => s.showToast);

  // Pan is local — same as Canvas.tsx.
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Vector-view delegation flag. Same gate Canvas.tsx uses; the
  // shared Toolbar's vector-toggle drives this when a vectorized
  // figure is active.
  const [vectorView, setVectorView] = useState(false);

  // Tool flow state (component-local). Mirrors Canvas.tsx's
  // collection of useState slots — Konva's interactivity layer
  // is the same UX, ported off SVG event bubbling onto Konva
  // shape ``onMouseDown`` listeners.
  const [arrowStart, setArrowStart] = useState<Pt | null>(null);
  const [curvedArrowStart, setCurvedArrowStart] = useState<Pt | null>(null);
  const [pendingText, setPendingText] = useState<Pt | null>(null);
  const [pendingTextValue, setPendingTextValue] = useState('');
  const [textFontSize, setTextFontSize] = useState(16);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const [bezierHandleDrag, setBezierHandleDrag] = useState<{
    primitiveId: string;
    field: 'start' | 'end' | 'control';
  } | null>(null);
  const [rectDragStart, setRectDragStart] = useState<Pt | null>(null);
  const [rectDragEnd, setRectDragEnd] = useState<Pt | null>(null);
  const isRectDragging = rectDragStart !== null;

  // ── Persistence-layer wiring ports from Canvas.tsx ───────────
  // IDB undo-stack hydration on first mount.
  useEffect(() => { void hydrateUndoStacksFromStorage(); }, []);

  // Two-tab cross-write detection on focus.
  useEffect(() => {
    if (!figure) return undefined;
    const onFocus = () => {
      void checkForCrossTabWrite(matterId, figure.id);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [matterId, figure]);

  // Viewport size — Konva needs explicit pixel dimensions on
  // ``Stage``. ResizeObserver tracks the wrapping div so the Stage
  // reflows when the side panels collapse / the window resizes.
  // Falls back to 0 until the first observer tick lands; the
  // initial render at 0×0 is invisible but harmless.
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    // Seed with the current measurement so the first paint can
    // size the Stage correctly without waiting for a resize event.
    setViewport({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setViewport({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset pan whenever the active figure changes — different
  // figures have different intrinsic sizes, so the previous pan
  // offset is meaningless for the new one. Centring happens once
  // the raster + viewport are both measured (effect below).
  const figureId = figure?.id ?? null;
  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [figureId]);

  // Raster URL + image element. Same endpoint Canvas.tsx hits.
  const rasterUrl = figure
    ? `/api/matter/${matterId}/drawing/figures/${figure.id}/raster`
    : null;
  const rasterImage = useRasterImage(rasterUrl);

  // Intrinsic figure size — prefer the persisted ``width/height``
  // on the row (Phase 5 stamps them at upload), fall back to the
  // natural image size once the raster decodes, and finally to a
  // sane default for figures that haven't loaded yet so the empty
  // state has a non-zero area.
  const figW = figure?.width
    || rasterImage?.naturalWidth
    || FALLBACK_W;
  const figH = figure?.height
    || rasterImage?.naturalHeight
    || FALLBACK_H;

  // Centre the figure inside the viewport on first measurement.
  // Re-runs when either the figure or the viewport size changes;
  // skips when the raster hasn't told us its size yet so the
  // centring math doesn't run against the fallback dimensions and
  // jump on raster-load.
  const centredOnceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!figureId) return;
    if (!viewport.w || !viewport.h) return;
    // Centre once per figure mount. Both raster + vector modes
    // share this; the SVG layer reads the same {scale, pan} as
    // the Konva Stage so they both reflow together on first
    // measurement. Don't gate on rasterImage — vector-only
    // figures (no raster) still need centring.
    if (centredOnceRef.current === figureId) return;
    const fitScale = _computeFitScale(
      viewport.w, viewport.h, figW, figH,
    );
    setZoom(fitScale);
    const scaledW = figW * fitScale;
    const scaledH = figH * fitScale;
    setPan({
      x: Math.round((viewport.w - scaledW) / 2),
      y: Math.round((viewport.h - scaledH) / 2),
    });
    centredOnceRef.current = figureId;
  }, [figureId, viewport.w, viewport.h, figW, figH, setZoom]);

  // Sort primitives by z_index ascending so Konva.Layer's child
  // order matches the SVG draw order. Memoised against the
  // primitives array reference — store updates always produce a
  // new array per the Zustand pattern, so this re-runs only when
  // the canvas state actually changes.
  const primitives: CanvasPrimitive[] = figureId
    ? canvasPrimitives[figureId] ?? []
    : [];
  const sortedPrimitives = useMemo(
    () => [...primitives].sort(
      (a, b) => (a.z_index ?? 0) - (b.z_index ?? 0),
    ),
    [primitives],
  );

  // Lasso bug: the onStageMouseUp commit closure was observed
  // reading primitivesCount=0 even though primitives were
  // rendering on screen. The deps array looked correct
  // (sortedPrimitives is included) but in practice the closure
  // was binding to an empty array. Mirror the live primitives
  // through a ref so the commit handler always reads the latest
  // value at mouseup time, independent of closure-refresh
  // semantics. The ref is kept current via a layout-effect that
  // runs synchronously after every render that changed
  // sortedPrimitives — guaranteed to be up to date before any
  // user event fires on the next tick.
  const primitivesRef = useRef<CanvasPrimitive[]>(sortedPrimitives);
  // BUG-001 Phase 1b — deep-equality guard. sortedPrimitives'
  // array reference changes on EVERY store write (Zustand) even
  // when the underlying primitive list is content-identical;
  // without the guard the effect re-fired 10-16x per state tick
  // and each re-fire emitted a console.log that PostHog's
  // MutationObserver (if recording is on) would capture, looping
  // back to a fresh DOM mutation. Compare the serialised content;
  // skip the assignment + log when nothing actually changed.
  const primitivesSerialisedRef = useRef<string>('');
  useEffect(() => {
    const serialised = JSON.stringify(sortedPrimitives);
    if (serialised === primitivesSerialisedRef.current) return;
    primitivesSerialisedRef.current = serialised;
    primitivesRef.current = sortedPrimitives;
    // DEV-only log — production builds drop it entirely. The
    // log was load-bearing during the lasso stale-closure
    // diagnosis but is noise once that's resolved.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[konva/lasso] primitivesRef updated', {
        figureId, length: sortedPrimitives.length,
      });
    }
  }, [sortedPrimitives, figureId]);

  // Wheel zoom toward the cursor. Konva's ``WheelEvent`` carries
  // the mouse position in screen space; we convert to canvas-
  // space (pre-zoom), bump scale, then re-derive the pan offset
  // so the same canvas-space point sits under the cursor after
  // the zoom. Standard "zoom toward cursor" pattern from the
  // Konva docs, adapted to our store-backed scale.
  const onWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    if (!stage) return;
    const oldScale = zoomLevel;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const direction = e.evt.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
    const nextScale = Math.max(
      ZOOM_MIN, Math.min(ZOOM_MAX, oldScale * direction),
    );
    if (nextScale === oldScale) return;
    // canvas-space point under the pointer at the current scale
    const mousePointTo = {
      x: (pointer.x - pan.x) / oldScale,
      y: (pointer.y - pan.y) / oldScale,
    };
    setZoom(nextScale);
    setPan({
      x: pointer.x - mousePointTo.x * nextScale,
      y: pointer.y - mousePointTo.y * nextScale,
    });
  }, [zoomLevel, pan, setZoom]);

  // Wheel containment — chunk-2 follow-up. Konva's onWheel
  // attaches to <Stage> only; in vector mode the Stage is
  // unmounted, so wheel events bubble straight to the page and
  // trigger browser zoom. Canvas.tsx solves this with a native
  // non-passive viewport-level listener (Canvas.tsx:399-415);
  // mirror the pattern here. The handler only ``preventDefault``s
  // — Konva's Stage onWheel still owns the zoom math in raster
  // mode (no double-firing because setZoom runs once); vector
  // mode has no zoom action, so the listener simply absorbs the
  // gesture so the page doesn't scroll.
  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return undefined;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
    };
    v.addEventListener('wheel', handler, { passive: false });
    return () => v.removeEventListener('wheel', handler);
  }, []);

  // Pan via Stage.draggable. We commit the pan on dragend so
  // intermediate dragmove events don't trigger a setState storm —
  // Konva renders the drag in flight via the internal stage
  // position, the React state catches up at the end. This matches
  // the existing Canvas.tsx pan UX (drag-pan only on the empty
  // viewport, not over a chip — Konva handles the latter via
  // ``listening={false}`` on every primitive in chunk 1, so the
  // primitives don't capture the pointer; the Stage gets the drag
  // event uninterrupted).
  const onDragEnd = useCallback((e: Konva.KonvaEventObject<DragEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    // Only respond when the Stage itself was the drag target —
    // ignore drags on child shapes (none should trigger today,
    // but a future interactive chunk may).
    if (e.target !== stage) return;
    setPan({ x: stage.x(), y: stage.y() });
  }, []);

  // ── Vector-mode SVG layer ───────────────────────────────────
  // When the figure is vectorised AND the drafter has toggled the
  // vector view, fetch the SVG once and stash it for inline
  // rendering BEHIND the transparent Konva Stage. Re-fetch is
  // driven by drawingStore.svgRefreshByFigure[figureId] (NOT
  // figure.updated_at, which canvas writes also bump — chunk-3
  // followup fix for the "vector flash" the original chunk
  // shipped with).
  const showVector = !!(figure?.is_vectorized && vectorView);
  const svgRefreshKey = useDrawingStore(
    (s) => (figureId ? s.svgRefreshByFigure[figureId] ?? 0 : 0),
  );
  // SEC-F01 (Phase 25 3-C) — sanitise the raw SVG bytes before
  // they hit dangerouslySetInnerHTML. ``USE_PROFILES: { svg: true,
  // svgFilters: true }`` keeps every legitimate SVG primitive
  // (paths, transforms, filters, gradients) while stripping
  // <script>, on* event handlers, and javascript: URLs. memoised
  // on svgContent so repeat renders don't re-parse.
  const sanitiseSvg = React.useCallback(
    (raw: string): string => DOMPurify.sanitize(raw, {
      USE_PROFILES: { svg: true, svgFilters: true },
    }) as unknown as string,
    [],
  );
  const svgContent = useFigureSvg(
    matterId,
    figureId,
    svgRefreshKey,
    showVector,
  );
  const svgLayerRef = useRef<HTMLDivElement | null>(null);
  const svgHitMap = useSvgHitMap(svgContent, svgLayerRef);
  // Multi-select for SVG elements in vector mode: cursor click
  // sets a one-element Set; lasso commit can populate N. Stored
  // as a Set so membership tests + delete-all-in-one-PATCH stay
  // O(1) per element.
  const [selectedSvgElementIds, setSelectedSvgElementIds] = useState<
    Set<string>
  >(() => new Set());
  // Keep a ref mirror so the keydown handler (registered against
  // window) can read the latest selection without re-binding on
  // every selection change. Same pattern the canvas-primitive
  // selection already uses.
  const selectedSvgElementIdsRef = useRef<Set<string>>(selectedSvgElementIds);
  useEffect(() => {
    selectedSvgElementIdsRef.current = selectedSvgElementIds;
  }, [selectedSvgElementIds]);
  const bumpSvgRefresh = useDrawingStore((s) => s.bumpSvgRefresh);
  // Reset SVG-element selection whenever the figure changes or
  // vector mode toggles off. Stale selection from one figure
  // would otherwise paint a phantom outline on the next.
  useEffect(() => {
    setSelectedSvgElementIds(new Set());
  }, [figureId, showVector]);
  // Tool-switch clears all selection state. The drafter's mental
  // model is "pick a tool, then act" — carrying a chip-selection
  // ring from cursor mode into lasso mode (or vice-versa) made
  // the toolbar feel "stuck on the previous tool." Switching to
  // ANY tool clears both the canvas-primitive selection AND the
  // SVG-element selection so the drafter starts fresh.
  useEffect(() => {
    clearSelection();
    setSelectedSvgElementIds(new Set());
  }, [activeTool, clearSelection]);
  // Delete-key SVG-element handler. Builds the new SVG body with
  // every selected element removed in a single DOMParser pass,
  // then one PATCH + one bumpSvgRefresh regardless of how many
  // elements were deleted. Bypasses re-rendering the SVG layer N
  // times for N deletes.
  const onDeleteSelectedSvgElements = useCallback(
    async () => {
      const ids = selectedSvgElementIdsRef.current;
      if (!figureId || !svgContent || ids.size === 0) return;
      const next = _removeElementsFromSvg(svgContent, ids);
      if (next === svgContent) return; // none matched
      const { patchDrawingFigureSvg } = await import('@/api/client');
      try {
        await patchDrawingFigureSvg(matterId, figureId, next);
        setSelectedSvgElementIds(new Set());
        bumpSvgRefresh(figureId);
      } catch (e: unknown) {
        showToast(
          `Vector edit failed: ${(e as { message?: string })?.message
            || 'unknown'}`,
        );
      }
    },
    [
      figureId, svgContent,
      matterId, bumpSvgRefresh, showToast,
    ],
  );

  // ── Pinch zoom — chunk-3 wiring. Konva doesn't auto-handle
  // pinch on Stage; track 2 pointers via touchmove and update
  // zoomLevel toward their midpoint. Mirrors the wheel-zoom
  // toward-cursor pattern so a pinch-to-zoom on the same point
  // keeps that point under the fingers. Stored in a ref so the
  // touchmove handler doesn't trigger a re-render every frame.
  const pinchRef = useRef<{
    distance: number; centreX: number; centreY: number;
  } | null>(null);
  const onTouchMove = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      const touches = e.evt.touches;
      if (touches.length !== 2) {
        pinchRef.current = null;
        return;
      }
      e.evt.preventDefault();
      const stage = e.target.getStage();
      if (!stage) return;
      const t1 = touches[0];
      const t2 = touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const rect = (stage.container() as HTMLDivElement).getBoundingClientRect();
      const centreX = ((t1.clientX + t2.clientX) / 2) - rect.left;
      const centreY = ((t1.clientY + t2.clientY) / 2) - rect.top;
      const prev = pinchRef.current;
      if (!prev || prev.distance <= 0) {
        pinchRef.current = { distance: dist, centreX, centreY };
        return;
      }
      const ratio = dist / prev.distance;
      const oldScale = zoomLevel;
      const nextScale = Math.max(
        ZOOM_MIN, Math.min(ZOOM_MAX, oldScale * ratio),
      );
      if (nextScale === oldScale) {
        pinchRef.current = { distance: dist, centreX, centreY };
        return;
      }
      const canvasX = (centreX - pan.x) / oldScale;
      const canvasY = (centreY - pan.y) / oldScale;
      setZoom(nextScale);
      setPan({
        x: centreX - canvasX * nextScale,
        y: centreY - canvasY * nextScale,
      });
      pinchRef.current = { distance: dist, centreX, centreY };
    },
    [zoomLevel, pan, setZoom],
  );
  const onTouchEnd = useCallback(() => {
    pinchRef.current = null;
  }, []);

  // ── Fit-to-screen handler — chunk-3 replacement for the
  // chunk-2 stub (was setZoom(1)). Computes a normalised fit
  // scale via _computeFitScale and recentres pan so the figure
  // sits in the middle of the viewport.
  const onFitToScreenAction = useCallback(() => {
    const fitScale = _computeFitScale(
      viewport.w, viewport.h, figW, figH,
    );
    setZoom(fitScale);
    const scaledW = figW * fitScale;
    const scaledH = figH * fitScale;
    setPan({
      x: Math.round((viewport.w - scaledW) / 2),
      y: Math.round((viewport.h - scaledH) / 2),
    });
  }, [viewport.w, viewport.h, figW, figH, setZoom]);

  // ── Coordinate helper ───────────────────────────────────────
  // Konva's Stage tracks scale + position; ``getRelativePointer
  // Position`` already returns canvas-local coords (post-scale,
  // post-pan). The wrapper exists so the call site reads
  // intent-first — `_canvasCoordsFromStage(stage)` rather than
  // an inline getRelativePointerPosition + null check at every
  // handler.
  const _canvasCoordsFromStage = useCallback(
    (stage: Konva.Stage | null): Pt | null => {
      if (!stage) return null;
      const p = stage.getRelativePointerPosition?.();
      return p ? { x: p.x, y: p.y } : null;
    },
    [],
  );

  // ── Frozen-figure guard (FE early-return so the autosave queue
  //    doesn't have to bounce a 409). Same toast Canvas.tsx
  //    surfaces; same set of tool gates. Chip drag + bezier
  //    reshape rely on the backend's 409 — keeps strict parity
  //    with Canvas.tsx, which doesn't FE-gate those paths
  //    either. ───────────────────────────────────────────────
  const _isFrozenForCreate = useCallback((): boolean => {
    if (!figure?.is_frozen) return false;
    if (
      activeTool === 'arrow'
      || activeTool === 'curved_arrow'
      || activeTool === 'text'
    ) {
      showToast(
        'This figure is frozen — click Unfreeze in the action '
        + 'bench before adding annotations.',
      );
      return true;
    }
    return false;
  }, [activeTool, figure, showToast]);

  // ── Stage background click router. Routes the empty-area
  //    click (no shape consumed it) to the active tool. Mirrors
  //    Canvas.tsx's onCanvasClick (line ~207) but driven by Konva's
  //    Stage 'click' event. The Stage receives the click only
  //    when no Konva.Group with listening=true caught it
  //    upstream — i.e. the drafter clicked empty raster. ─────
  const onStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;
      // Per-shape onMouseDown / onClick handlers cancelBubble,
      // so the Stage's onClick only fires when no listening
      // shape consumed the event. The earlier strict
      // ``e.target === stage || === Image`` gate excluded clicks
      // that landed on the interactive primitives Layer's
      // transparent areas (e.target was the Layer, not the
      // Stage) and silently no-op'd arrow / curved / text
      // tool background placement. Drop the gate.
      if (!figure) return;
      if (_isFrozenForCreate()) return;
      const c = _canvasCoordsFromStage(stage);
      if (!c) return;
      if (activeTool === 'arrow') {
        if (arrowStart === null) {
          setArrowStart(c);
        } else {
          upsertCanvasPrimitive(matterId, figure.id, {
            primitive_type: 'arrow',
            primitive_data_json: {
              x1: arrowStart.x, y1: arrowStart.y,
              x2: c.x, y2: c.y,
            },
            z_index: sortedPrimitives.length,
          });
          setArrowStart(null);
        }
        return;
      }
      if (activeTool === 'curved_arrow') {
        if (curvedArrowStart === null) {
          setCurvedArrowStart(c);
        } else {
          const { cx, cy } = defaultBezierControl(
            curvedArrowStart.x, curvedArrowStart.y, c.x, c.y,
          );
          upsertCanvasPrimitive(matterId, figure.id, {
            primitive_type: 'curved_arrow',
            primitive_data_json: {
              x1: curvedArrowStart.x, y1: curvedArrowStart.y,
              x2: c.x, y2: c.y,
              cx, cy,
            },
            z_index: sortedPrimitives.length,
          });
          setCurvedArrowStart(null);
        }
        return;
      }
      if (activeTool === 'text') {
        setPendingText(c);
        setPendingTextValue('');
        return;
      }
      // Cursor / lasso / zoom: bare-click on background just
      // clears the prior selection (matches Canvas.tsx).
      if (activeTool === 'cursor' || activeTool === 'lasso') {
        clearSelection();
      }
    },
    [
      activeTool, arrowStart, curvedArrowStart, figure,
      matterId, _canvasCoordsFromStage, _isFrozenForCreate,
      upsertCanvasPrimitive, clearSelection,
    ],
  );

  // ── Stage mousedown — kicks off the rect drag for cursor /
  //    lasso tools when the click lands on background. The
  //    rect's role is unified between cursor + lasso (multi-
  //    select via AABB overlap on release). Other tools'
  //    background interactions land on onStageClick instead. ──
  const onStageMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;
      // No strict ``e.target === stage`` gate — the original
      // chunk-2 check excluded clicks that landed on the
      // interactive primitives Layer's transparent area
      // (e.target was the Layer, not the Stage), which caused
      // lasso to silently no-op. Per-shape mousedown handlers
      // already call e.cancelBubble = true, so the Stage
      // handler ONLY fires when no shape consumed the event —
      // making the explicit target check redundant.
      if (activeTool !== 'cursor' && activeTool !== 'lasso') return;
      // Clear any previous selection regardless of tool — a
      // background mousedown ends prior selection in both modes.
      clearSelection();
      // Vector-element selection also clears on background — the
      // hit-Rect's onMouseDown stops bubbling, so reaching here
      // means the click missed every SVG element.
      setSelectedSvgElementIds(new Set());
      // Cursor tool background-mousedown: stop here so Konva's
      // Stage drag-pan owns the gesture (stageDraggable is true
      // for cursor; pan starts on the same mousedown). Don't
      // start a rect-drag — that's lasso's job, and starting
      // one here would conflict with the pan (chunk-2 followup
      // dropped this for exactly that reason).
      if (activeTool === 'cursor') return;
      // Lasso tool: start the rect-drag for multi-select.
      const c = _canvasCoordsFromStage(stage);
      if (!c) return;
      // Diagnostic logging — pair with [konva/lasso] commit
      // logs above. Remove once the lasso-doesn't-select bug
      // is rooted.
      // PERF-F02 (Phase 25 3-D) — dev-only guard so the log
      // doesn't fire in production. Console writes trigger DOM
      // mutations that activate PostHog's MutationObserver
      // (BUG-001 compositor freeze in the drawing module).
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[konva/lasso] start', { activeTool, c });
      }
      setRectDragStart(c);
      setRectDragEnd(c);
      // Lasso's stageDraggable is false (cursor + zoom only),
      // so no pan-vs-rect conflict here, but cancelBubble keeps
      // Konva's hit-test from triggering anything else.
      e.cancelBubble = true;
    },
    [activeTool, _canvasCoordsFromStage, clearSelection],
  );

  // ── Stage mousemove — tracks the in-flight rect drag (cursor
  //    + lasso) and the bezier-handle reshape. Chip move is
  //    handled per-shape via Konva's onDragMove (see
  //    KonvaInteractiveNumeral / KonvaInteractiveArrow — they
  //    use Konva.Group `draggable` + onDragMove which the
  //    drafter doesn't see in this handler). ────────────────
  const onStageMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;
      if (isRectDragging && rectDragStart) {
        const c = _canvasCoordsFromStage(stage);
        if (c) setRectDragEnd(c);
        return;
      }
      if (bezierHandleDrag && figure) {
        const c = _canvasCoordsFromStage(stage);
        if (!c) return;
        const p = sortedPrimitives.find(
          (q) => q.id === bezierHandleDrag.primitiveId,
        );
        if (!p) return;
        const fieldMap = {
          start:   { kx: 'x1', ky: 'y1' },
          end:     { kx: 'x2', ky: 'y2' },
          control: { kx: 'cx', ky: 'cy' },
        }[bezierHandleDrag.field];
        upsertCanvasPrimitive(matterId, figure.id, {
          id: p.id,
          primitive_type: p.primitive_type,
          primitive_data_json: {
            ...(p.primitive_data_json as Record<string, unknown>),
            [fieldMap.kx]: c.x,
            [fieldMap.ky]: c.y,
          },
          z_index: p.z_index,
          drawing_numeral_id: p.drawing_numeral_id,
        });
      }
    },
    [
      _canvasCoordsFromStage, isRectDragging, rectDragStart,
      bezierHandleDrag, figure, matterId,
      sortedPrimitives, upsertCanvasPrimitive,
    ],
  );

  // ── Stage mouseup — commits the rect drag (selects every
  //    primitive whose AABB overlaps the rect) and releases the
  //    bezier handle if one was being dragged. ───────────────
  const onStageMouseUp = useCallback(() => {
    // Diagnostic logging — log BOTH the closure-bound and ref-bound
    // primitives so a future regression where the closure stales
    // is visible in the same line. The ref is the load-bearing
    // source for the matching loop below.
    const livePrimitives = primitivesRef.current;
    if (isRectDragging && rectDragStart && rectDragEnd) {
      // PERF-F02 (Phase 25 3-D) — dev-only guard.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[konva/lasso] commit fired', {
          activeTool,
          rectStart: rectDragStart,
          rectEnd:   rectDragEnd,
          primitivesCount: livePrimitives.length,
          primitivesCount_closure: sortedPrimitives.length,
        });
      }
    }
    if (isRectDragging && rectDragStart && rectDragEnd) {
      // Hard gate: only commit a rect-drag when the active tool
      // is actually lasso. Defence in depth — the rect-drag
      // start path already gates on lasso, but if rectDragStart
      // somehow got set under cursor (stale state, future code
      // drift), we don't want a phantom multi-select to fire.
      // Cursor's drag is pure pan; never select.
      if (activeTool !== 'lasso') {
        // PERF-F02 (Phase 25 3-D) — dev-only guard.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log(
            '[konva/lasso] commit short-circuited: activeTool=',
            activeTool,
          );
        }
        setRectDragStart(null);
        setRectDragEnd(null);
        if (bezierHandleDrag) {
          setBezierHandleDrag(null);
          endCanvasMutationGroup();
        }
        return;
      }
      const rect = {
        minX: Math.min(rectDragStart.x, rectDragEnd.x),
        maxX: Math.max(rectDragStart.x, rectDragEnd.x),
        minY: Math.min(rectDragStart.y, rectDragEnd.y),
        maxY: Math.max(rectDragStart.y, rectDragEnd.y),
      };
      const w = rect.maxX - rect.minX;
      const h = rect.maxY - rect.minY;
      // PERF-F02 (Phase 25 3-D) — dev-only guard.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[konva/lasso] rect bounds', { rect, w, h });
      }
      // 5px threshold — matches Canvas.tsx so a click-without-
      // drag clears selection rather than picking up the
      // primitive under the click via a zero-area rect.
      if (w > 5 || h > 5) {
        // Vector-mode branch: AABB-test svgHitMap entries against
        // the lasso rect. Entry bboxes are in SVG-internal coords;
        // the lasso rect is in canvas-pixel coords. Project each
        // entry bbox through the same scaleX/scaleY the Konva
        // <Group> uses (figW/internalW, figH/internalH) so the
        // comparison happens in canvas coords. Selecting SVG
        // elements bypasses the canvas-primitive selection — the
        // two layers don't coexist in vector mode in practice.
        if (
          showVector
          && svgHitMap.entries.length > 0
          && svgHitMap.internalW > 0 && svgHitMap.internalH > 0
        ) {
          const sx = figW / svgHitMap.internalW;
          const sy = figH / svgHitMap.internalH;
          // Phase 2b — coverage-ratio guard. Vectoriser-traced SVGs
          // typically include a background path (el-0 in the trace
          // output) whose bbox spans the entire viewBox; without
          // this guard a tiny lasso drag inevitably AABB-intersects
          // it and 8+ glyph fragments get selected as collateral.
          // 50% threshold catches the background span without
          // dropping legitimate large primitives (e.g. an outline
          // frame around a 40-numeral figure can comfortably cover
          // 30-40% of the canvas).
          const canvasArea = figW * figH;
          const matchedSvg = new Set<string>();
          let coverageSkipped = 0;
          for (const entry of svgHitMap.entries) {
            const ebMinX = entry.bbox.x * sx;
            const ebMinY = entry.bbox.y * sy;
            const ebMaxX = (entry.bbox.x + entry.bbox.width) * sx;
            const ebMaxY = (entry.bbox.y + entry.bbox.height) * sy;
            const elArea = (ebMaxX - ebMinX) * (ebMaxY - ebMinY);
            if (canvasArea > 0 && elArea / canvasArea > 0.5) {
              coverageSkipped += 1;
              continue;
            }
            const overlaps =
              ebMinX <= rect.maxX && ebMaxX >= rect.minX
              && ebMinY <= rect.maxY && ebMaxY >= rect.minY;
            if (overlaps) matchedSvg.add(entry.editId);
          }
          // PERF-F02 (Phase 25 3-D) — dev-only guard.
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[konva/lasso] svg matches', {
              count: matchedSvg.size,
              ids:   Array.from(matchedSvg),
              coverage_skipped: coverageSkipped,
            });
          }
          setSelectedSvgElementIds(matchedSvg);
          setRectDragStart(null);
          setRectDragEnd(null);
          if (bezierHandleDrag) {
            setBezierHandleDrag(null);
            endCanvasMutationGroup();
          }
          return;
        }
        // Raster-mode branch: existing canvas-primitive path.
        const matched: string[] = [];
        // Read from the ref, not the closure. This is the fix:
        // even if the useCallback's closure binds an empty
        // sortedPrimitives, the ref reflects the latest render's
        // value at the moment the user releases the mouse.
        for (const p of livePrimitives) {
          const bbox = primitiveBoundingBox(p);
          if (!bbox) continue;
          const overlaps =
            bbox.minX <= rect.maxX && bbox.maxX >= rect.minX
            && bbox.minY <= rect.maxY && bbox.maxY >= rect.minY;
          if (overlaps) matched.push(p.id);
        }
        // PERF-F02 (Phase 25 3-D) — dev-only guard.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[konva/lasso] matches', {
            count: matched.length,
            ids:   matched,
          });
        }
        setSelectedIds(matched);
      } else {
        // PERF-F02 (Phase 25 3-D) — dev-only guard.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log(
            '[konva/lasso] threshold-skipped (w/h <= 5)',
            { w, h },
          );
        }
      }
      setRectDragStart(null);
      setRectDragEnd(null);
    }
    if (bezierHandleDrag) {
      setBezierHandleDrag(null);
      endCanvasMutationGroup();
    }
  }, [
    activeTool,
    isRectDragging, rectDragStart, rectDragEnd,
    sortedPrimitives, setSelectedIds,
    bezierHandleDrag, endCanvasMutationGroup,
    showVector, svgHitMap, figW, figH,
  ]);

  // ── Per-primitive drag (cursor tool). Selecting a primitive
  //    + dragging it moves the shape; one mutation group per
  //    drag so undo restores in a single step. The actual
  //    Konva.Group `draggable` lives on each interactive shape
  //    component (below); these callbacks centralise the store
  //    updates. ────────────────────────────────────────────
  const onPrimitiveDragStart = useCallback(
    (p: CanvasPrimitive, c: Pt) => {
      if (!figure) return;
      setSelectedIds([p.id]);
      setDraggingId(p.id);
      const data = p.primitive_data_json as Record<string, unknown>;
      const px = (data.x as number) ?? 0;
      const py = (data.y as number) ?? 0;
      dragOffsetRef.current = { dx: c.x - px, dy: c.y - py };
      beginCanvasMutationGroup(matterId, figure.id);
    },
    [figure, matterId, setSelectedIds, beginCanvasMutationGroup],
  );

  const onPrimitiveDragMove = useCallback(
    (p: CanvasPrimitive, c: Pt) => {
      if (!figure) return;
      const off = dragOffsetRef.current || { dx: 0, dy: 0 };
      const nx = c.x - off.dx;
      const ny = c.y - off.dy;
      // Only x/y-anchored primitives move via this path
      // (numeral + text). Arrows aren't part of the chip-drag
      // UX in Canvas.tsx — preserve parity.
      upsertCanvasPrimitive(matterId, figure.id, {
        id: p.id,
        primitive_type: p.primitive_type,
        primitive_data_json: {
          ...(p.primitive_data_json as Record<string, unknown>),
          x: nx, y: ny,
        },
        z_index: p.z_index,
        drawing_numeral_id: p.drawing_numeral_id,
      });
    },
    [figure, matterId, upsertCanvasPrimitive],
  );

  const onPrimitiveDragEnd = useCallback(() => {
    setDraggingId(null);
    dragOffsetRef.current = null;
    endCanvasMutationGroup();
  }, [endCanvasMutationGroup]);

  // Bezier handle pointerdown — opens a mutation group for the
  // duration of the reshape so undo restores in one step.
  const onBezierHandleDown = useCallback(
    (primitiveId: string, field: 'start' | 'end' | 'control') => {
      if (!figure) return;
      beginCanvasMutationGroup(matterId, figure.id);
      setBezierHandleDrag({ primitiveId, field });
    },
    [figure, matterId, beginCanvasMutationGroup],
  );

  // ── Pending-text overlay commit/cancel ─────────────────────
  const commitPendingText = useCallback(() => {
    if (!figure || !pendingText) return;
    const text = pendingTextValue.trim();
    if (text) {
      upsertCanvasPrimitive(matterId, figure.id, {
        primitive_type: 'text',
        primitive_data_json: {
          x: pendingText.x, y: pendingText.y, text,
          fontSize: textFontSize,
        },
        z_index: sortedPrimitives.length,
      });
    }
    setPendingText(null);
    setPendingTextValue('');
  }, [
    figure, matterId, pendingText, pendingTextValue,
    sortedPrimitives.length, textFontSize, upsertCanvasPrimitive,
  ]);

  const cancelPendingText = useCallback(() => {
    setPendingText(null);
    setPendingTextValue('');
  }, []);

  // ── Term-drag drop. Matches the Canvas.tsx handler: a term
  //    dropped on the canvas creates a text primitive at the
  //    drop point with the term's name. ────────────────────
  const [dropHover, setDropHover] = useState(false);
  const onWrapperDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes(DRAWING_TERM_DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDropHover(true);
    }, [],
  );
  const onWrapperDragLeave = useCallback(() => setDropHover(false), []);
  const onWrapperDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      setDropHover(false);
      const raw = e.dataTransfer.getData(DRAWING_TERM_DRAG_TYPE);
      if (!raw || !figure) return;
      e.preventDefault();
      let payload: DraggedTermPayload | null = null;
      try {
        payload = JSON.parse(raw) as DraggedTermPayload;
      } catch {
        return;
      }
      if (!payload || !payload.name?.trim()) return;
      // Translate the drop point (client coords) into canvas
      // coords. The viewport's top-left is at pan.{x,y} and the
      // figure scales by zoomLevel.
      const wrapper = viewportRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const cx = (e.clientX - rect.left - pan.x) / zoomLevel;
      const cy = (e.clientY - rect.top - pan.y) / zoomLevel;
      upsertCanvasPrimitive(matterId, figure.id, {
        primitive_type: 'text',
        primitive_data_json: { x: cx, y: cy, text: payload.name },
        z_index: sortedPrimitives.length,
      });
    },
    [
      figure, matterId, pan, zoomLevel,
      sortedPrimitives.length, upsertCanvasPrimitive,
    ],
  );

  // ── Keyboard shortcuts ─────────────────────────────────────
  // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z (or Ctrl+Y) redo, Escape
  // clears in-flight tool state, Delete/Backspace deletes
  // selection. Window-scoped (matches Canvas.tsx — chip clicks
  // don't focus the parent <div tabIndex>, so a div-scoped
  // handler would silently miss the key). Skipped when typing
  // in an input/textarea so the pending-text overlay keeps
  // working.
  const selectedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    selectedIdsRef.current = selectedPrimitiveIds;
  }, [selectedPrimitiveIds]);
  useEffect(() => {
    if (!figure) return undefined;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable = target && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.isContentEditable
      );
      if (e.key === 'Escape' && !inEditable) {
        setArrowStart(null);
        setCurvedArrowStart(null);
        setPendingText(null);
        setPendingTextValue('');
        clearSelection();
        return;
      }
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod) {
        const key = e.key.toLowerCase();
        if (key === 'z' && !e.shiftKey) {
          if (inEditable) return;
          e.preventDefault();
          if (canUndoFn(figure.id)) undoCanvas(matterId, figure.id);
          return;
        }
        if ((key === 'z' && e.shiftKey) || key === 'y') {
          if (inEditable) return;
          e.preventDefault();
          if (canRedoFn(figure.id)) redoCanvas(matterId, figure.id);
          return;
        }
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (inEditable) return;
        // Vector-mode SVG element deletion: cursor OR lasso tool
        // + at least one selected SVG element → PATCH the SVG
        // with all selected elements removed. Takes precedence
        // over canvas-primitive deletion when SVG elements are
        // selected; otherwise falls through to the primitive-
        // delete path.
        if (
          (activeTool === 'cursor' || activeTool === 'lasso')
          && showVector
          && selectedSvgElementIdsRef.current.size > 0
        ) {
          if (figure.is_frozen) {
            showToast(
              'Figure is frozen — click Unfreeze before editing the vector layer.',
            );
            return;
          }
          e.preventDefault();
          void onDeleteSelectedSvgElements();
          return;
        }
        const ids = selectedIdsRef.current;
        if (ids.length === 0) return;
        if (figure.is_frozen) {
          showToast(
            'Figure is frozen — click Unfreeze before deleting primitives.',
          );
          return;
        }
        e.preventDefault();
        deletePrimitives(matterId, figure.id, ids);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    figure, matterId, canUndoFn, canRedoFn, undoCanvas, redoCanvas,
    deletePrimitives, clearSelection, showToast,
    activeTool, showVector,
    onDeleteSelectedSvgElements,
  ]);

  // Cursor for the wrapping viewport — different per active
  // tool so the drafter sees crosshair / text / move cursors.
  const wrapperCursor = (() => {
    if (activeTool === 'arrow' || activeTool === 'curved_arrow') return 'crosshair';
    if (activeTool === 'text') return 'text';
    if (activeTool === 'lasso') return 'crosshair';
    if (activeTool === 'zoom') return 'zoom-in';
    return 'default';
  })();

  // Stage.draggable: chunk-2 follow-up — drop the cursor-tool
  // pan because it raced with the lasso/rect-drag. With cursor +
  // lasso both starting a rect drag on background, Konva's
  // Stage.draggable activates from the same mousedown event and
  // eats the move + up sequence — selection never commits.
  // Canvas.tsx has the same constraint (its viewport-level pan
  // only fires when the click lands OUTSIDE the figure surface);
  // matching that intent here keeps the rect-drag gesture clean.
  // Pan moves to the dedicated zoom tool — and to cursor too,
  // per the chunk-3 followup. Cursor + zoom both pan the Stage
  // on a background drag; lasso owns the rect-drag for
  // multi-select. The two never conflict because cursor's
  // background-mousedown handler (onStageMouseDown) no longer
  // starts a rect-drag — it only clears the prior selection
  // and lets Konva's Stage drag-pan run uninterrupted. Wheel
  // zoom from chunk 1 still works on every tool.
  const stageDraggable = (
    (activeTool === 'cursor' || activeTool === 'zoom')
    && !isRectDragging
    && draggingId === null
    && bezierHandleDrag === null
  );

  // Chunk 3 — vector delegation absorbed. The chrome (Toolbar /
  // ActionBench / StatusStrip) renders unconditionally; the
  // surface area always mounts a Konva Stage, with an HTML SVG
  // layer rendered BEHIND the Stage when ``showVector`` is true.
  // ``showVector`` is computed earlier (where the SVG fetch
  // hook reads it) and is in scope here.

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="konva-canvas"
    >
      <Toolbar
        activeTool={activeTool}
        onToolChange={setActiveTool as (t: DrawingTool) => void}
        zoomLevel={zoomLevel}
        onZoomChange={setZoom}
        onFitToScreen={onFitToScreenAction}
        canUndo={figure ? canUndoFn(figure.id) : false}
        canRedo={figure ? canRedoFn(figure.id) : false}
        onUndo={() => figure && undoCanvas(matterId, figure.id)}
        onRedo={() => figure && redoCanvas(matterId, figure.id)}
        textFontSize={textFontSize}
        onTextFontSizeChange={setTextFontSize}
        figureIsVectorized={!!figure?.is_vectorized}
        vectorView={vectorView}
        onToggleVectorView={() => setVectorView((v) => !v)}
      />
      <div
        ref={viewportRef}
        className={[
          'relative flex-1 overflow-hidden bg-zinc-50',
          dropHover ? 'ring-2 ring-[#002FA7]/40' : '',
        ].join(' ')}
        data-testid={figure ? 'konva-canvas-surface' : 'konva-canvas-empty'}
        style={{ cursor: wrapperCursor }}
        onDragOver={onWrapperDragOver}
        onDragLeave={onWrapperDragLeave}
        onDrop={onWrapperDrop}
      >
        {figure ? (
          <>
            {/* Vector layer — chunk 3. SVG sits BEHIND the
                transparent Konva Stage; both share the same
                {scale, pan} transform so toggling between
                modes feels seamless and chunk-2 primitives
                render uniformly on top of either mode.
                pointer-events: none so the drafter's clicks
                always reach the Konva Stage (the chunk-2 tool
                handlers + per-primitive listeners). Click-to-
                select-and-Delete on individual SVG elements
                (chunk-3 followup vector minor edits) is wired
                via the Konva hit layer below — the SVG layer
                stays display-only. */}
            {showVector && svgContent && (
              <div
                className="pointer-events-none absolute left-0 top-0"
                data-testid="konva-canvas-vector-layer"
                style={{
                  transformOrigin: '0 0',
                  transform: (
                    `translate(${pan.x}px, ${pan.y}px) `
                    + `scale(${zoomLevel})`
                  ),
                  width: figW,
                  height: figH,
                }}
              >
                <div
                  ref={svgLayerRef}
                  className="[&>svg]:h-full [&>svg]:w-full"
                  style={{ width: figW, height: figH }}
                  dangerouslySetInnerHTML={{
                    __html: sanitiseSvg(svgContent),
                  }}
                />
              </div>
            )}
            <Stage
              width={viewport.w}
              height={viewport.h}
              x={pan.x}
              y={pan.y}
              scaleX={zoomLevel}
              scaleY={zoomLevel}
              draggable={stageDraggable}
              onWheel={onWheel}
              onDragEnd={onDragEnd}
              onClick={onStageClick}
              onTap={onStageClick}
              onMouseDown={onStageMouseDown}
              onTouchStart={onStageMouseDown}
              onMouseMove={onStageMouseMove}
              onTouchMove={(e) => {
                onTouchMove(e);
                onStageMouseMove(e);
              }}
              onMouseUp={onStageMouseUp}
              onTouchEnd={(e) => {
                onTouchEnd();
                onStageMouseUp();
              }}
            >
              {/* Raster layer renders ONLY in raster mode. In
                  vector mode the SVG layer below already shows
                  the figure body. */}
              {!showVector && (
                <Layer listening={false}>
                  {rasterImage && (
                    <Image
                      image={rasterImage}
                      x={0}
                      y={0}
                      width={figW}
                      height={figH}
                    />
                  )}
                </Layer>
              )}

              {/* Interactive primitives layer. Each shape is
                  individually listening so its onMouseDown can
                  capture the gesture; otherwise the gesture
                  bubbles to the Stage handler above. Same UX
                  contract as Canvas.tsx's per-primitive
                  pointerdown + stopPropagation pattern. */}
              {/* Vector hit layer. Mounts in vector mode under
                  cursor OR lasso. Click Rects (transparent +
                  listening) are cursor-only — they MUST NOT
                  mount under lasso else they steal mousedown
                  from the rect-drag start. The highlight Rect
                  renders for every selected element regardless
                  of the active tool so lasso multi-select
                  shows what got picked. */}
              {showVector
                && (activeTool === 'cursor' || activeTool === 'lasso')
                && svgHitMap.entries.length > 0
                && svgHitMap.internalW > 0 && svgHitMap.internalH > 0 && (
                <Layer>
                  <Group
                    scaleX={figW / svgHitMap.internalW}
                    scaleY={figH / svgHitMap.internalH}
                  >
                    {svgHitMap.entries.map((entry) => {
                      const isSel = selectedSvgElementIds.has(entry.editId);
                      return (
                        <Group key={entry.editId}>
                          {activeTool === 'cursor' && (
                            <Rect
                              x={entry.bbox.x}
                              y={entry.bbox.y}
                              width={entry.bbox.width}
                              height={entry.bbox.height}
                              fill="rgba(0,0,0,0)"
                              listening
                              onMouseDown={(e) => {
                                e.cancelBubble = true;
                                setSelectedSvgElementIds(
                                  new Set([entry.editId]),
                                );
                              }}
                              onTouchStart={(e) => {
                                e.cancelBubble = true;
                                setSelectedSvgElementIds(
                                  new Set([entry.editId]),
                                );
                              }}
                            />
                          )}
                          {isSel && (
                            <Rect
                              x={entry.bbox.x}
                              y={entry.bbox.y}
                              width={entry.bbox.width}
                              height={entry.bbox.height}
                              fill="rgba(0, 47, 167, 0.08)"
                              stroke="#002FA7"
                              strokeWidth={2}
                              dash={[4, 3]}
                              listening={false}
                              strokeScaleEnabled={false}
                            />
                          )}
                        </Group>
                      );
                    })}
                  </Group>
                </Layer>
              )}
              <Layer>
                {sortedPrimitives.map((p) => renderInteractivePrimitive(
                  p, {
                    numerals,
                    selected: selectedPrimitiveIds.includes(p.id),
                    cursorToolActive: activeTool === 'cursor',
                    inVectorMode: showVector,
                    isFrozen: !!figure.is_frozen,
                    canvasCoords: _canvasCoordsFromStage,
                    onPrimitiveDragStart,
                    onPrimitiveDragMove,
                    onPrimitiveDragEnd,
                    onBezierHandleDown,
                    setSelectedIds,
                  },
                ))}
                {/* Transient rect drag preview (cursor + lasso). */}
                {activeTool === 'lasso' && isRectDragging && rectDragStart && rectDragEnd && (
                  <Rect
                    x={Math.min(rectDragStart.x, rectDragEnd.x)}
                    y={Math.min(rectDragStart.y, rectDragEnd.y)}
                    width={Math.abs(rectDragEnd.x - rectDragStart.x)}
                    height={Math.abs(rectDragEnd.y - rectDragStart.y)}
                    fill="rgba(0, 47, 167, 0.06)"
                    stroke="#002FA7"
                    strokeWidth={1}
                    dash={[4, 4]}
                    listening={false}
                    strokeScaleEnabled={false}
                  />
                )}
              </Layer>
            </Stage>

            {/* HTML <input> overlay for the text tool. Positioned
                in screen pixels (post-pan, post-scale) so it
                tracks the canvas point exactly. Mirrors
                Canvas.tsx's overlay (line ~1436). */}
            {pendingText && (
              <input
                type="text"
                autoFocus
                value={pendingTextValue}
                onChange={(e) => setPendingTextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitPendingText();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelPendingText();
                  }
                }}
                onBlur={() => {
                  if (pendingTextValue.trim()) commitPendingText();
                  else cancelPendingText();
                }}
                className="absolute rounded-sm border border-[#002FA7] bg-white px-1 py-0.5 shadow-sm focus:outline-none"
                style={{
                  left: pan.x + pendingText.x * zoomLevel,
                  top: pan.y + pendingText.y * zoomLevel
                       - textFontSize * 0.75 * zoomLevel,
                  minWidth: 120,
                  fontSize: textFontSize * zoomLevel,
                }}
                data-testid="konva-canvas-pending-text"
              />
            )}

            {!rasterImage && rasterUrl && (
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-zinc-400"
                aria-hidden
                data-testid="konva-canvas-loading"
              >
                Loading figure…
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-zinc-500">
            Select a figure from the gallery.
          </div>
        )}
      </div>
      <ActionBench matterId={matterId} figure={figure} />
      <StatusStrip
        figure={figure}
        primitives={primitives}
        numerals={numerals}
        figures={figures}
      />
    </div>
  );
}


type NumeralRow = ReturnType<typeof useDrawingStore.getState>['numerals'][number];


// ─── Interactive primitive renderer (chunk 2) ────────────────
// Per-shape Konva.Group with its own listening logic. Cursor
// tool routes mouseDown to selection + drag; non-cursor tools
// pass the gesture through to the Stage handler so the
// background behaviours (e.g. arrow click placement on top of an
// existing chip — not actually a real UX in Canvas.tsx, but the
// pass-through is the closest parity) keep working.

interface InteractiveCtx {
  numerals: NumeralRow[];
  selected: boolean;
  cursorToolActive: boolean;
  isFrozen: boolean;
  /** True in vector mode — disables per-primitive draggable so
   *  Stage.draggable (pan) wins on a background drag even when
   *  the pointer lands on a chip / arrow / text. Drafters
   *  switch to raster mode to MOVE primitives; in vector mode
   *  they stay clickable for selection but not drag-grabbable. */
  inVectorMode: boolean;
  canvasCoords: (stage: Konva.Stage | null) => Pt | null;
  onPrimitiveDragStart: (p: CanvasPrimitive, c: Pt) => void;
  onPrimitiveDragMove: (p: CanvasPrimitive, c: Pt) => void;
  onPrimitiveDragEnd: () => void;
  onBezierHandleDown: (
    primitiveId: string, field: 'start' | 'end' | 'control',
  ) => void;
  setSelectedIds: (ids: string[]) => void;
}

function renderInteractivePrimitive(
  p: CanvasPrimitive, ctx: InteractiveCtx,
): React.ReactNode {
  // NB-09 — the numeral is printed in the image; no chip to draw.
  if (isRasterAnchor(p)) return null;
  switch (p.primitive_type) {
    case 'numeral':
      return <KonvaInteractiveNumeral key={p.id} primitive={p} ctx={ctx} />;
    case 'arrow':
      return <KonvaInteractiveArrow key={p.id} primitive={p} ctx={ctx} />;
    case 'curved_arrow':
      return (
        <KonvaInteractiveCurvedArrow key={p.id} primitive={p} ctx={ctx} />
      );
    case 'text':
      return <KonvaInteractiveText key={p.id} primitive={p} ctx={ctx} />;
    case 'lasso':
      // Legacy persisted-lasso primitive (the current FE never
      // emits one, but DB rows from older versions still need to
      // render). Keep the read-only polygon rendering — no
      // interaction.
      return <KonvaLasso key={p.id} primitive={p} />;
    default:
      return null;
  }
}


function KonvaInteractiveNumeral({
  primitive, ctx,
}: { primitive: CanvasPrimitive; ctx: InteractiveCtx }) {
  const data = primitive.primitive_data_json as {
    x?: number; y?: number; numeral?: string; label?: string;
  };
  const x = data.x ?? 0;
  const y = data.y ?? 0;
  const registryRow = primitive.drawing_numeral_id
    ? ctx.numerals.find((n) => n.id === primitive.drawing_numeral_id) || null
    : null;
  const numeralText = registryRow?.numeral || data.numeral || '?';
  const isOrphan = !primitive.drawing_numeral_id
    || (registryRow && !registryRow.bound_claim_id);
  const isStale = !!registryRow?.is_stale;
  const fill = isStale
    ? '#a1a1aa'
    : isOrphan
      ? '#fbbf24'
      : '#002FA7';
  const textFill = isStale ? '#3f3f46' : '#ffffff';
  const display = `${numeralText}${isOrphan && !isStale ? '?' : ''}`;
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // Only the cursor tool grabs primitives; arrow/curved/text
    // tools fall through so the click lands on the Stage handler.
    if (!ctx.cursorToolActive) return;
    if (ctx.isFrozen) return;
    e.cancelBubble = true;
    // Vector mode: select-only path. Don't open a mutation group
    // or set draggingId — Konva.Group's draggable is false in
    // vector mode (Stage drag-pan owns background drags), so
    // onPrimitiveDragMove / End would never fire and the
    // mutation group would leak. Direct setSelectedIds keeps the
    // chip clickable for selection without the drag plumbing.
    if (ctx.inVectorMode) {
      ctx.setSelectedIds([primitive.id]);
      return;
    }
    const stage = e.target.getStage();
    const c = ctx.canvasCoords(stage);
    if (!c) return;
    ctx.onPrimitiveDragStart(primitive, c);
  };
  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!ctx.cursorToolActive) return;
    const stage = e.target.getStage();
    const c = ctx.canvasCoords(stage);
    if (!c) return;
    ctx.onPrimitiveDragMove(primitive, c);
  };
  const handleDragEnd = () => {
    if (!ctx.cursorToolActive) return;
    ctx.onPrimitiveDragEnd();
  };
  return (
    <Group
      x={x} y={y}
      listening
      draggable={ctx.cursorToolActive && !ctx.isFrozen && !ctx.inVectorMode}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      {ctx.selected && (
        <Circle
          radius={28} stroke="#002FA7" strokeWidth={3} opacity={0.6}
          strokeScaleEnabled={false}
        />
      )}
      <Circle radius={20} fill={fill} />
      <Text
        x={-20} y={-20} width={40} height={40}
        text={display}
        align="center" verticalAlign="middle"
        fontFamily="ui-monospace, monospace"
        fontSize={isOrphan && !isStale ? 13 : 14}
        fontStyle="600"
        fill={textFill}
        listening={false}
      />
    </Group>
  );
}


function KonvaInteractiveArrow({
  primitive, ctx,
}: { primitive: CanvasPrimitive; ctx: InteractiveCtx }) {
  const d = primitive.primitive_data_json as {
    x1?: number; y1?: number; x2?: number; y2?: number;
  };
  const x1 = d.x1 ?? 0;
  const y1 = d.y1 ?? 0;
  const x2 = d.x2 ?? 0;
  const y2 = d.y2 ?? 0;
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!ctx.cursorToolActive) return;
    e.cancelBubble = true;
    ctx.setSelectedIds([primitive.id]);
  };
  return (
    <Group
      listening
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
    >
      {/* Wide invisible stroke as a fat hit target so a thin
          1.5px line is still grabbable. Only enabled when cursor
          is active so it doesn't swallow background clicks. */}
      {ctx.cursorToolActive && (
        <Arrow
          points={[x1, y1, x2, y2]}
          stroke="rgba(0,0,0,0)"
          strokeWidth={14}
          fill="rgba(0,0,0,0)"
          pointerLength={0}
          pointerWidth={0}
          listening
          strokeScaleEnabled={false}
        />
      )}
      <Arrow
        points={[x1, y1, x2, y2]}
        stroke={ctx.selected ? '#002FA7' : '#000000'}
        strokeWidth={1.5}
        fill={ctx.selected ? '#002FA7' : '#000000'}
        pointerLength={10}
        pointerWidth={8}
        listening={false}
        strokeScaleEnabled={false}
      />
    </Group>
  );
}


function KonvaInteractiveCurvedArrow({
  primitive, ctx,
}: { primitive: CanvasPrimitive; ctx: InteractiveCtx }) {
  const d = primitive.primitive_data_json as {
    x1?: number; y1?: number; x2?: number; y2?: number;
    cx?: number; cy?: number;
  };
  const x1 = d.x1 ?? 0;
  const y1 = d.y1 ?? 0;
  const x2 = d.x2 ?? 0;
  const y2 = d.y2 ?? 0;
  const { cx, cy } = (d.cx != null && d.cy != null)
    ? { cx: d.cx, cy: d.cy }
    : defaultBezierControl(x1, y1, x2, y2);
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!ctx.cursorToolActive) return;
    e.cancelBubble = true;
    ctx.setSelectedIds([primitive.id]);
  };
  return (
    <Group
      listening
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
    >
      {/* Fat invisible curve hit target — same rationale as the
          straight-arrow path. */}
      {ctx.cursorToolActive && (
        <Arrow
          points={quadBezierPoints(x1, y1, cx, cy, x2, y2)}
          stroke="rgba(0,0,0,0)"
          strokeWidth={14}
          fill="rgba(0,0,0,0)"
          pointerLength={0}
          pointerWidth={0}
          listening
          strokeScaleEnabled={false}
        />
      )}
      <Arrow
        points={quadBezierPoints(x1, y1, cx, cy, x2, y2)}
        stroke={ctx.selected ? '#002FA7' : '#000000'}
        strokeWidth={1.5}
        fill={ctx.selected ? '#002FA7' : '#000000'}
        pointerLength={10}
        pointerWidth={8}
        tension={0}
        listening={false}
        strokeScaleEnabled={false}
      />
      {/* Bezier handles render only when this curve is selected
          and the cursor tool is active — same UX as Canvas.tsx. */}
      {ctx.selected && ctx.cursorToolActive && !ctx.isFrozen && (
        <>
          <KonvaBezierHandle
            cx={x1} cy={y1} shape="square"
            onMouseDown={(e) => {
              e.cancelBubble = true;
              ctx.onBezierHandleDown(primitive.id, 'start');
            }}
          />
          <KonvaBezierHandle
            cx={x2} cy={y2} shape="square"
            onMouseDown={(e) => {
              e.cancelBubble = true;
              ctx.onBezierHandleDown(primitive.id, 'end');
            }}
          />
          <KonvaBezierHandle
            cx={cx} cy={cy} shape="circle"
            onMouseDown={(e) => {
              e.cancelBubble = true;
              ctx.onBezierHandleDown(primitive.id, 'control');
            }}
          />
        </>
      )}
    </Group>
  );
}


/** Small reshape handle — square endpoints + round control.
 *  White fill + blue stroke matches Canvas.tsx's BezierHandle.
 *  The actual bezier reshape is driven by Stage.onMouseMove
 *  (component-level) so this just opens a mutation group on
 *  pointerdown. */
function KonvaBezierHandle({
  cx, cy, shape, onMouseDown,
}: {
  cx: number; cy: number;
  shape: 'square' | 'circle';
  onMouseDown: (e: Konva.KonvaEventObject<MouseEvent>) => void;
}) {
  if (shape === 'circle') {
    return (
      <Circle
        x={cx} y={cy} radius={6}
        fill="#ffffff" stroke="#002FA7" strokeWidth={2}
        listening
        onMouseDown={onMouseDown}
        onTouchStart={onMouseDown}
        strokeScaleEnabled={false}
      />
    );
  }
  return (
    <Rect
      x={cx - 5} y={cy - 5} width={10} height={10}
      fill="#ffffff" stroke="#002FA7" strokeWidth={2}
      listening
      onMouseDown={onMouseDown}
      onTouchStart={onMouseDown}
      strokeScaleEnabled={false}
    />
  );
}


function KonvaInteractiveText({
  primitive, ctx,
}: { primitive: CanvasPrimitive; ctx: InteractiveCtx }) {
  const d = primitive.primitive_data_json as {
    x?: number; y?: number; text?: string; fontSize?: number;
  };
  const x = d.x ?? 0;
  const y = d.y ?? 0;
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!ctx.cursorToolActive) return;
    if (ctx.isFrozen) return;
    e.cancelBubble = true;
    // Vector mode: select-only (same rationale as KonvaInteractive
    // Numeral above). Don't open a mutation group; Konva won't
    // fire dragmove/dragend with draggable=false.
    if (ctx.inVectorMode) {
      ctx.setSelectedIds([primitive.id]);
      return;
    }
    const stage = e.target.getStage();
    const c = ctx.canvasCoords(stage);
    if (!c) return;
    ctx.onPrimitiveDragStart(primitive, c);
  };
  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!ctx.cursorToolActive) return;
    const stage = e.target.getStage();
    const c = ctx.canvasCoords(stage);
    if (!c) return;
    ctx.onPrimitiveDragMove(primitive, c);
  };
  const handleDragEnd = () => {
    if (!ctx.cursorToolActive) return;
    ctx.onPrimitiveDragEnd();
  };
  return (
    <Group
      x={x} y={y}
      listening
      draggable={ctx.cursorToolActive && !ctx.isFrozen && !ctx.inVectorMode}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <Text
        x={0} y={0}
        text={d.text || ''}
        fontSize={d.fontSize ?? 16}
        fontFamily="ui-sans-serif, sans-serif"
        fill={ctx.selected ? '#002FA7' : '#000000'}
        listening={false}
      />
    </Group>
  );
}


// ─── Read-only renderers (kept for the vector-edit fallback +
//     potential read-only embeds) ───────────────────────────
function renderPrimitive(
  p: CanvasPrimitive, numerals: NumeralRow[],
): React.ReactNode {
  if (isRasterAnchor(p)) return null;
  switch (p.primitive_type) {
    case 'numeral':   return <KonvaNumeral   key={p.id} primitive={p} numerals={numerals} />;
    case 'arrow':     return <KonvaArrow     key={p.id} primitive={p} />;
    case 'curved_arrow': return <KonvaCurvedArrow key={p.id} primitive={p} />;
    case 'text':      return <KonvaText      key={p.id} primitive={p} />;
    case 'lasso':     return <KonvaLasso     key={p.id} primitive={p} />;
    default:          return null;
  }
}


/** Numeral chip — circle + centered label. Read-only port of
 *  Canvas.tsx's NumeralChip; deliberately omits the focus pulse,
 *  hover tooltip, stale hatch, and selection ring (those are
 *  interaction affordances landing in chunk 2). Colours match the
 *  SVG version: blue fill for normal, amber for orphan. The stale
 *  case falls back to grey so the rendering stays meaningful even
 *  without the SVG ``<pattern>`` hatch fill. */
function KonvaNumeral({
  primitive, numerals,
}: { primitive: CanvasPrimitive; numerals: NumeralRow[] }) {
  const data = primitive.primitive_data_json as {
    x?: number; y?: number; numeral?: string; label?: string;
  };
  const x = data.x ?? 0;
  const y = data.y ?? 0;
  const registryRow = primitive.drawing_numeral_id
    ? numerals.find((n) => n.id === primitive.drawing_numeral_id) || null
    : null;
  const numeralText = registryRow?.numeral || data.numeral || '?';
  const isOrphan = !primitive.drawing_numeral_id
    || (registryRow && !registryRow.bound_claim_id);
  const isStale = !!registryRow?.is_stale;
  const fill = isStale
    ? '#a1a1aa'
    : isOrphan
      ? '#fbbf24'
      : '#002FA7';
  const textFill = isStale ? '#3f3f46' : '#ffffff';
  const display = `${numeralText}${isOrphan && !isStale ? '?' : ''}`;
  return (
    <Group x={x} y={y} listening={false}>
      <Circle radius={20} fill={fill} />
      {/* Konva.Text positions by top-left, so we shift by
          (-width/2, -height/2) via offsetX/offsetY to centre. The
          width/height we pass match the visual square the
          ``align``/``verticalAlign`` props lay the text inside. */}
      <Text
        x={-20}
        y={-20}
        width={40}
        height={40}
        text={display}
        align="center"
        verticalAlign="middle"
        fontFamily="ui-monospace, monospace"
        fontSize={isOrphan && !isStale ? 13 : 14}
        fontStyle="600"
        fill={textFill}
      />
    </Group>
  );
}


/** Arrow — straight line + Konva-rendered arrowhead. Konva.Arrow
 *  takes a flat ``points`` array (``[x1,y1,x2,y2]``) and draws the
 *  pointed head at the second point natively, so we don't need
 *  the SVG ``<marker>`` definition Canvas.tsx uses. */
function KonvaArrow({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    x1?: number; y1?: number; x2?: number; y2?: number;
  };
  return (
    <Arrow
      points={[d.x1 ?? 0, d.y1 ?? 0, d.x2 ?? 0, d.y2 ?? 0]}
      stroke="#000000"
      strokeWidth={1.5}
      fill="#000000"
      pointerLength={10}
      pointerWidth={8}
      listening={false}
      strokeScaleEnabled={false}
    />
  );
}


/** Curved arrow — quadratic-bezier polyline + arrowhead at the
 *  end. Konva.Arrow doesn't accept a curve directly so we sample
 *  the bezier with ``_quadBezierPoints`` and pass the resulting
 *  flat-points array to Konva.Arrow, which draws the head at the
 *  last segment's endpoint with the correct tangent direction. */
function KonvaCurvedArrow({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    x1?: number; y1?: number; x2?: number; y2?: number;
    cx?: number; cy?: number;
  };
  const x1 = d.x1 ?? 0;
  const y1 = d.y1 ?? 0;
  const x2 = d.x2 ?? 0;
  const y2 = d.y2 ?? 0;
  const { cx, cy } = (d.cx != null && d.cy != null)
    ? { cx: d.cx, cy: d.cy }
    : defaultBezierControl(x1, y1, x2, y2);
  return (
    <Arrow
      points={quadBezierPoints(x1, y1, cx, cy, x2, y2)}
      stroke="#000000"
      strokeWidth={1.5}
      fill="#000000"
      pointerLength={10}
      pointerWidth={8}
      tension={0}
      listening={false}
      strokeScaleEnabled={false}
    />
  );
}


/** Text annotation — single Konva.Text. Default fontSize matches
 *  Canvas.tsx (16px) when the row doesn't override it. */
function KonvaText({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    x?: number; y?: number; text?: string; fontSize?: number;
  };
  return (
    <Text
      x={d.x ?? 0}
      y={d.y ?? 0}
      text={d.text || ''}
      fontSize={d.fontSize ?? 16}
      fontFamily="ui-sans-serif, sans-serif"
      fill="#000000"
      listening={false}
    />
  );
}


/** Lasso polygon — closed shape with a soft fill + dashed stroke.
 *  Konva.Line with ``closed=true`` + a flat points array matches
 *  the SVG <polygon> visual contract. */
function KonvaLasso({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    points?: Array<{ x: number; y: number }>;
  };
  const flat: number[] = [];
  for (const pt of (d.points || [])) {
    flat.push(pt.x, pt.y);
  }
  if (flat.length < 6) return null; // <3 points; would render degenerately
  return (
    <Line
      points={flat}
      closed
      fill="rgba(0, 0, 0, 0.04)"
      stroke="#000000"
      strokeWidth={1}
      dash={[6, 4]}
      listening={false}
      strokeScaleEnabled={false}
    />
  );
}


// Konva.Rect is the transient selection-rectangle painted during
// the cursor / lasso rect-drag (chunk 2).
